import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Paper,
  Button,
  Dialog,
  TextField,
  Typography,
  DialogTitle,
  DialogContent,
  CircularProgress,
} from "@material-ui/core";
import Popup from "./Popup/Popup";
import QrReader from "react-qr-reader";
import useStyles from "./UpdateDepartments.styles";
import apiService from "../../../services/apiService";
import WebcamCapture from "../../media/WebcamCapture/WebcamCapture";
import DepartmentHistory from "./DepartmentHistory/DepartmentHistory";

const UpdateDepartments = ({ User }) => {
  const classes = useStyles();

  const [img, setImg] = useState(null);
  const [buttonImg, setButtonImg] = useState(false);
  const [actions, setActions] = useState([]);
  const [idInput, setIdInput] = useState("");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedLine, setSelectedLine] = useState(null);

  const [webcamDisplay, setWebcamDisplay] = useState(false);

  const [scanDialogOpen, setScanDialogOpen] = useState(false);

  const allNull = () => {
    setWebcamDisplay(false);
  };

  const showWebcam = () => {
    allNull();
    setWebcamDisplay(true);
  };

  const addImg = () => {
    if (!img || !user) {
      alert("אין תמונה או משתמש לעדכון");
      return;
    }

    const obj = { img: img, UpdateDate: new Date(), UserUpd: User.nickname };
    const config = { headers: { Authorization: `Bearer ${User.Token}` } };
    const url = `/people/${user._id}`;

    apiService
      .put(url, obj, config)
      .then((res) => {
        if (res.data.Status === "200") {
          alert("התמונה נוספה בהצלחה");
          setButtonImg(false);
          setWebcamDisplay(false);
          loadUserById(user.PersonID || user.idNumber);
        } else {
          alert("שגיאה: " + res.data.Results);
        }
      })
      .catch((err) => {
        console.error("שגיאה בהעלאת תמונה", err);
      });
  };

  const actionColors = {
    1: "#FFCDD2",
    2: "#F8BBD0",
    3: "#E1BEE7",
    4: "#D1C4E9",
    5: "#C5CAE9",
    6: "#BBDEFB",
    7: "#FFCDD2",
    8: "#F8BBD0",
  };

  useEffect(() => {
    const fetchActions = async () => {
      try {
        const response = await apiService.get("/departmentActions");
        setActions(response.data);
      } catch (err) {
        console.error("שגיאה בטעינת רשימת הפעולות", err);
      }
    };
    fetchActions();
  }, []);

  const onLineClick = (line) => {
    if (!user) {
      setError("נא להזין ת.ז קודם");
      return;
    }
    setSelectedLine(line);
    setOpenDialog(true);
  };

  const loadUserById = async (id) => {
    if (!id) return;
    setLoading(true);
    setError(null);

    try {
      const { data } = await apiService.get(`/people/by-id-or-customer/${id}`);
      setUser(Array.isArray(data) && data.length ? data[0] : null);
      if (!data.length) setError("משתמש לא נמצא");
      setHistoryKey((prev) => prev + 1);
    } catch (err) {
      setUser(null);
      setError(err.response?.data?.message || "שגיאה בטעינת המשתמש");
    } finally {
      setLoading(false);
    }
  };

  const onIdSubmit = (e) => {
    e.preventDefault();
    loadUserById(idInput.trim());
    setWebcamDisplay(false);
  };

  const onIdChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) setIdInput(value);
  };

  useEffect(() => {
    if (user?.img) {
      setImg(user.img);
      setButtonImg(false);
    } else {
      setImg(null);
      setButtonImg(false);
    }
  }, [user]);

  const handleScan = (data) => {
    if (data) {
      try {
        const parsed = JSON.parse(data);
        const idNumber = parsed.PersonID || parsed.CustomerID;

        if (idNumber) {
          loadUserById(idNumber);
          setScanDialogOpen(false);
        } else {
          console.error("QR לא מכיל PersonID או CustomerID תקין");
          alert("ברקוד לא תקין");
        }
      } catch (err) {
        console.error("שגיאה בפירוק הברקוד:", err);
        alert("לא הצלחנו לקרוא את הברקוד. ודא שהוא תקין.");
      }
    }
  };

  const handleError = (err) => {
    console.error("שגיאה בסריקה", err);
  };

  return (
    <div className={"updateDepartments " + classes.container}>
      <form onSubmit={onIdSubmit} className="form">
        <TextField
          fullWidth
          variant="outlined"
          placeholder="ת.ז או מספר חבר"
          value={idInput}
          onChange={onIdChange}
          inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
        />

        <Box display="flex" justifyContent="center" m={2}>
          <Box ml={2}>
            <Button variant="contained" color="default" onClick={() => setScanDialogOpen(true)}>
              סריקה
            </Button>
          </Box>
          <Button type="submit" variant="contained" color="primary" disabled={loading}>
            טען פרטים
          </Button>
        </Box>
      </form>

      <Dialog open={scanDialogOpen} onClose={() => setScanDialogOpen(false)} fullWidth>
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Box className={classes.dialogTitleBox}>
            <Button
              onClick={() => setScanDialogOpen(false)}
              className={classes.scanDialog}
            >
              ✖
            </Button>
            <Typography variant="h6">סריקת הברקוד</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <QrReader
            delay={500}
            onError={handleError}
            onScan={handleScan}
            style={{ width: '100%' }}
            showViewFinder={true}
          />
        </DialogContent>
      </Dialog>

      {loading && <CircularProgress style={{ margin: 20 }} />}
      {error && <Typography color="error">{error}</Typography>}

      {user ? (
        <>
          <Paper className={classes.paper}>
            <div className={classes.userInfoContainer}>
              <div className={classes.userDetails}>
                <Typography>
                  <b>ת.ז:</b> {user.idNumber || user.PersonID || ""}
                </Typography>
                <Typography>
                  <b>מספר חבר:</b> {user.CustomerID || ""}
                </Typography>
                <Typography>
                  <b>שם פרטי:</b> {user.firstName || user.FirstName || ""}
                </Typography>
                <Typography>
                  <b>שם משפחה:</b> {user.lastName || user.LastName || ""}
                </Typography>
                <Typography>
                  <b>כתובת:</b>{" "}
                  {[user?.lkh_Street, user?.City].filter(Boolean).join(", ") || "—"}
                </Typography>
                <Typography>
                  <b>טלפון:</b> {user.Cell || user.Phone || ""}
                </Typography>
              </div>

              <div className={classes.imageSection}>
                <div className={classes.imageWrapper}>
                  {webcamDisplay ? (
                    <div className={classes.webcamContainer}>
                      <WebcamCapture
                        changeImg={(newImg) => {
                          setImg(newImg);
                          setButtonImg(true);
                        }}
                        Img={img}
                        smallSize={true}
                      />
                    </div>
                  ) : img ? (
                    <img src={img} alt="user" className={classes.userImage} />
                  ) : (
                    <Typography className={classes.noImageText}>אין תמונה</Typography>
                  )}
                </div>

                {!buttonImg ? (
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={showWebcam}
                    className={classes.updateImageBtn}
                  >
                    שנה תמונה
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={addImg}
                    className={classes.updateImageBtn}
                  >
                    עדכן תמונה
                  </Button>
                )}
              </div>
            </div>
          </Paper>
          <Grid container spacing={2}>
            {actions.map((line) => (
              <Grid item xs={6} key={line._id}>
                <div
                  onClick={() => onLineClick(line)}
                  className={classes.lineItem}
                  style={{ backgroundColor: actionColors[line._id] || "#eee" }}
                >
                  <span>{line.actionName}</span>
                </div>
              </Grid>
            ))}
          </Grid>

          <DepartmentHistory
            personId={user.PersonID}
            key={historyKey}
            actionColors={actionColors}
          />

          <Popup
            selectedLine={selectedLine}
            setSelectedLine={setSelectedLine}
            openDialog={openDialog}
            setOpenDialog={setOpenDialog}
            setLoading={setLoading}
            user={user}
            User={User}
            setError={setError}
            setHistoryKey={setHistoryKey}
          />
        </>
      ) : (
        !loading && (
          <Typography className={classes.centeredText}>
            נא להזין ת.ז וללחוץ "טען פרטים"
          </Typography>
        )
      )}
    </div>
  );
};

export default UpdateDepartments;
